/* eslint-disable default-param-last */
import { ProjectFilterRequest } from '@mercator-gcp/types/elastic_search_service/ProjectFilterRequest';
import ElasticSearchFilterAPI from 'services/API/ElasticSearchFilterAPI';
import { GroupRaw } from 'types';
import { getDefaultEndDate } from 'utils/constants';
import { Region } from 'utils/constants/location';
import { formatBounds } from 'utils/format';

export function boundsToLiteral(
  bounds?: google.maps.LatLngBounds,
): google.maps.LatLngBoundsLiteral {
  if (!bounds) return {} as google.maps.LatLngBoundsLiteral;

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  return {
    north: ne.lat(),
    east: ne.lng(),
    south: sw.lat(),
    west: sw.lng(),
  };
}

export function literalToBounds(
  literal?: google.maps.LatLngBoundsLiteral,
): google.maps.LatLngBounds {
  if (!literal) return {} as google.maps.LatLngBounds;

  const { north, east, south, west } = literal;
  const response = new google.maps.LatLngBounds(
    { lat: south, lng: west },
    { lat: north, lng: east },
  );
  if (response) {
    return response;
  }

  return {} as google.maps.LatLngBounds;
}

export function boundsStringToLiteral(
  boundsString?: string | undefined,
): google.maps.LatLngBoundsLiteral {
  if (!boundsString) return {} as google.maps.LatLngBoundsLiteral;

  const [north, east, south, west] = boundsString.split(',').map(Number);

  return {
    north,
    east,
    south,
    west,
  };
}

export async function fetchListByBbox(
  bbox = '',
  page = 1,
  mercatorMetropolitanId?: Region | null,
  pageSize = 5,
) {
  let paramObj = {};
  if (bbox.length > 0) {
    paramObj = {
      ...(bbox && { bbox }),
      ...(page && { page }),
      ...(mercatorMetropolitanId && { mercator_metropolitan_id: mercatorMetropolitanId }),
      ...(pageSize && { page_size: pageSize }),
    };
  }
  const response = await ElasticSearchFilterAPI.postFilterProjects({
    page,
    ...paramObj,
  });

  return {
    nextPage: response.data.next,
    projects: response.data.results,
    count: response.data.count,
  };
}

export async function fetchList(
  address = '',
  page = 1,
  mercatorMetropolitanId?: Region | null,
  pageSize = 5,
  clusterId?: string,
) {
  let paramObj = {};
  if (address.length > 0 || clusterId) {
    paramObj = {
      ...(address && { address }),
      ...(clusterId && { cluster_id: clusterId }),
      ...(page && { page }),
      ...(mercatorMetropolitanId && { mercator_metropolitan_id: mercatorMetropolitanId }),
      ...(pageSize && { page_size: pageSize }),
    };
  }
  const response = await ElasticSearchFilterAPI.postFilterProjects({
    page,
    ...paramObj,
  });

  return {
    nextPage: response.data.next,
    projects: response.data.results,
    count: response.data.count,
  };
}

export async function fetchItem() {
  return {};
}

type FetchAddressListProps = {
  pathOrBounds?: google.maps.LatLngBoundsLiteral | string;
  page?: number;
  search?: string;
  address_search?: string;
  districts?: number[];
  zoneExclusions?: number[];
  createdAtStart?: string;
  createdAtEnd?: string;
  verticals?: number[];
  verticalsExclusions?: number[];
  triggers?: GroupRaw['triggers'];
  past_project_stage?: GroupRaw['past_project_stages'];
  exclude_past_project_stage?: GroupRaw['exclude_past_project_stages'];
  min_project_cost?: number;
  max_project_cost?: number;
  include_unknown_cost?: GroupRaw['project_cost_include_unknown'];
  status?: string;
  keywords?: string;
  zoomQuery?: string;
  startDate?: string;
  endDate?: string;
  realEstate?: boolean;
  brokerData?: boolean;
  documentUrl?: boolean;
  address?: string;
  cluster_id?: string;
  radius?: any;
  exclusions?: string;
  companies?: number[];
  signal?: AbortSignal;
  sort?: string[];
  project_size_min?: GroupRaw['project_size_min'];
  project_size_max?: GroupRaw['project_size_max'];
  project_size_include_unknown?: GroupRaw['project_size_include_unknown'];
  parcel_size_min?: GroupRaw['parcel_size_min'];
  parcel_size_max?: GroupRaw['parcel_size_max'];
  parcel_size_include_unknown?: GroupRaw['parcel_size_include_unknown'];
  work_types?: GroupRaw['work_types'];
  work_types_exclusions?: GroupRaw['work_types_exclusions'];
  company_keywords?: GroupRaw['company_keywords'];
  company_keywords_exclusions?: GroupRaw['company_keywords_exclusions'];
  city_ids?: number[];
  mercator_metropolitan_id?: string | null;
  mercator_metropolitan_ids?: string[] | null;
  company_stakeholder_types?: GroupRaw['company_stakeholder_types'];
  company_stakeholder_types_exclusions?: GroupRaw['company_stakeholder_types_exclusions'];
  includeDocumentsInSearch?: boolean;
  county_codes?: ProjectFilterRequest['county_codes'];
  // Raw ES query bodies for a scoutbot-sourced saved search. When present,
  // these are sent instead of the individual filter fields above (which
  // stay empty for a scoutbot search, since editing only happens via chat) -
  // only the map view (bbox) and the date/status fields below still apply.
  raw_queries?: Record<string, { query: Record<string, unknown> }> | null;
};

export async function fetchAddressList({
  pathOrBounds,
  page = 1,
  search,
  districts,
  zoneExclusions,
  createdAtStart,
  createdAtEnd,
  verticals,
  verticalsExclusions,
  triggers,
  past_project_stage,
  exclude_past_project_stage,
  min_project_cost,
  max_project_cost,
  include_unknown_cost,
  status,
  keywords,
  startDate,
  realEstate,
  brokerData,
  documentUrl,
  address,
  cluster_id,
  radius,
  exclusions,
  companies,
  signal,
  sort,
  project_size_min,
  project_size_max,
  project_size_include_unknown,
  parcel_size_min,
  parcel_size_max,
  parcel_size_include_unknown,
  work_types,
  work_types_exclusions,
  company_keywords,
  company_keywords_exclusions,
  city_ids,
  mercator_metropolitan_id,
  mercator_metropolitan_ids,
  company_stakeholder_types,
  company_stakeholder_types_exclusions,
  includeDocumentsInSearch,
  address_search,
  county_codes,
  raw_queries,
}: FetchAddressListProps) {
  // make post request with data to get address list
  const defaultEndDate = getDefaultEndDate();

  let formattedBounds = pathOrBounds;
  if (typeof pathOrBounds !== 'string' && !pathOrBounds?.east) {
    formattedBounds = undefined;
  }

  const bbox =
    !formattedBounds || typeof formattedBounds === 'string'
      ? formattedBounds
      : formatBounds(formattedBounds);

  if (raw_queries) {
    const rawPayload = {
      bbox,
      raw_queries,
      ...(status && { status }),
      ...(createdAtStart && { date_created_start: createdAtStart }),
      ...(createdAtEnd && { date_created_end: createdAtEnd }),
      latest_activity_start: startDate,
      latest_activity_end: defaultEndDate,
      signal,
      page,
    };

    const { data } = includeDocumentsInSearch
      ? await ElasticSearchFilterAPI.postFilterProjectsAndDocuments(rawPayload)
      : await ElasticSearchFilterAPI.postFilterProjects(rawPayload);

    return {
      nextPage: data.next,
      projects: data.results,
      count: data.count,
    };
  }

  let metropolitanIds = {};
  if (mercator_metropolitan_ids) {
    metropolitanIds = { mercator_metropolitan_ids };
  } else if (mercator_metropolitan_id) {
    metropolitanIds = { mercator_metropolitan_id };
  }

  const filterPayload: ProjectFilterRequest = {
    bbox,
    ...metropolitanIds,
    ...(page && { page }),
    ...(search &&
      search.length > 0 &&
      radius && { radius: radius.radius, point: radius.point }),
    ...(address_search && { address_search }),
    ...(min_project_cost && { min_project_cost }),
    ...(max_project_cost && { max_project_cost }),
    include_unknown_cost,
    ...(status && { status }),
    ...(districts && { districts }),
    ...(zoneExclusions && { zone_exclusions: zoneExclusions }),
    ...(createdAtStart && { date_created_start: createdAtStart }),
    ...(createdAtEnd && { date_created_end: createdAtEnd }),
    ...(keywords && { keywords }),
    ...(exclusions && { exclusions }),
    ...(triggers && { triggers }),
    ...(past_project_stage && { past_project_stage }),
    ...(exclude_past_project_stage && { exclude_past_project_stage }),
    ...(verticals && { verticals }),
    ...(verticalsExclusions && { verticals_exclusions: verticalsExclusions }),
    latest_activity_start: startDate,
    latest_activity_end: defaultEndDate,
    ...(address && { address }),
    ...(cluster_id && { cluster_id }),
    ...(realEstate && { real_estate_data_available: realEstate }),
    ...(brokerData && { broker_data_available: brokerData }),
    ...(documentUrl && { document_url_available: documentUrl }),
    ...(companies && { companies }),
    ...(sort && { sort }),
    project_size: {
      min: project_size_min || null,
      max: project_size_max || null,
      include_unknown: !!project_size_include_unknown,
    },
    parcel_size: {
      min: parcel_size_min || null,
      max: parcel_size_max || null,
      include_unknown: !!parcel_size_include_unknown,
    },
    ...(work_types && { work_type_ids: work_types }),
    ...(work_types_exclusions && { work_type_ids_exclusions: work_types_exclusions }),
    ...(company_keywords && { company_keywords }),
    ...(company_keywords_exclusions && { company_keywords_exclusions }),
    ...(city_ids && { city_ids }),
    ...(county_codes && { county_codes }),
    company_types: company_stakeholder_types ? company_stakeholder_types.split(',') : [],
    company_types_exclusions: company_stakeholder_types_exclusions
      ? company_stakeholder_types_exclusions?.split(',')
      : [],
  };

  if (includeDocumentsInSearch) {
    const { data } = await ElasticSearchFilterAPI.postFilterProjectsAndDocuments({
      ...filterPayload,
      signal,
      page,
    });

    const response = data;

    return {
      nextPage: response.next,
      projects: response.results,
      count: response.count,
    };
  }

  const { data } = await ElasticSearchFilterAPI.postFilterProjects({
    ...filterPayload,
    signal,
    page,
  });

  const response = data;

  return {
    nextPage: response.next,
    projects: response.results,
    count: response.count,
  };
}

type MapPinRequest = {
  pathOrBounds?: google.maps.LatLngBoundsLiteral | string;
  search?: string;
  districts?: number[];
  zoneExclusions?: number[];
  createdAtStart?: string;
  createdAtEnd?: string;
  verticals?: number[];
  verticalsExclusions?: number[];
  triggers?: GroupRaw['triggers'];
  past_project_stage?: GroupRaw['past_project_stages'];
  exclude_past_project_stage?: GroupRaw['exclude_past_project_stages'];
  min_project_cost?: number;
  max_project_cost?: number;
  include_unknown_cost?: ProjectFilterRequest['include_unknown_cost'];
  status?: string;
  keywords?: string;
  zoomQuery?: string;
  startDate?: string;
  endDate?: string;
  real_estate_data_available?: boolean;
  broker_data_available?: boolean;
  document_url_available?: boolean;
  radius?: any;
  exclusions?: string;
  companies?: number[];
  signal?: AbortSignal;
  project_size_min?: GroupRaw['project_size_min'];
  project_size_max?: GroupRaw['project_size_max'];
  project_size_include_unknown?: GroupRaw['project_size_include_unknown'];
  parcel_size_min?: GroupRaw['parcel_size_min'];
  parcel_size_max?: GroupRaw['parcel_size_max'];
  parcel_size_include_unknown?: GroupRaw['parcel_size_include_unknown'];
  work_types?: GroupRaw['work_types'];
  work_types_exclusions?: GroupRaw['work_types_exclusions'];
  company_keywords?: GroupRaw['company_keywords'];
  company_keywords_exclusions?: GroupRaw['company_keywords_exclusions'];
  city_ids?: number[];
  county_codes?: string[];
  mercator_metropolitan_id?: string | null;
  mercator_metropolitan_ids?: string[] | null;
  company_stakeholder_types?: GroupRaw['company_stakeholder_types'];
  company_stakeholder_types_exclusions?: GroupRaw['company_stakeholder_types_exclusions'];
  // Raw ES query bodies for a scoutbot-sourced saved search. When present,
  // these are sent instead of the individual filter fields above (which
  // stay empty for a scoutbot search, since editing only happens via chat) -
  // only the map view (bbox) and the date/status fields below still apply.
  raw_queries?: Record<string, { query: Record<string, unknown> }> | null;
};

export async function fetchMapPins({
  pathOrBounds,
  search,
  districts,
  zoneExclusions,
  createdAtStart,
  createdAtEnd,
  verticals,
  verticalsExclusions,
  triggers,
  past_project_stage,
  exclude_past_project_stage,
  min_project_cost,
  max_project_cost,
  include_unknown_cost,
  status,
  keywords,
  startDate,
  real_estate_data_available,
  broker_data_available,
  document_url_available,
  radius,
  exclusions,
  companies,
  signal,
  project_size_min,
  project_size_max,
  project_size_include_unknown,
  parcel_size_min,
  parcel_size_max,
  parcel_size_include_unknown,
  work_types,
  work_types_exclusions,
  company_keywords,
  company_keywords_exclusions,
  city_ids,
  county_codes,
  mercator_metropolitan_ids,
  company_stakeholder_types,
  company_stakeholder_types_exclusions,
  raw_queries,
}: MapPinRequest) {
  const defaultEndDate = getDefaultEndDate();

  let formattedBounds = pathOrBounds;
  if (typeof pathOrBounds !== 'string' && !pathOrBounds?.east) {
    formattedBounds = undefined;
  }

  const bbox =
    !formattedBounds || typeof formattedBounds === 'string'
      ? formattedBounds
      : formatBounds(formattedBounds);

  if (raw_queries) {
    const { data } = await ElasticSearchFilterAPI.postFilterProjectPins({
      bbox,
      raw_queries,
      ...(status && { status }),
      ...(createdAtStart && { date_created_start: createdAtStart }),
      ...(createdAtEnd && { date_created_end: createdAtEnd }),
      latest_activity_start: startDate,
      latest_activity_end: defaultEndDate,
      signal,
    });
    if (data.message && !data.data.results) {
      throw new Error(data.message);
    }

    return data.data.results || [];
  }

  const filterPayload: ProjectFilterRequest = {
    bbox,
    ...(search && search.length > 0 && { radius: radius.radius, point: radius.point }),
    ...(min_project_cost && { min_project_cost }),
    ...(max_project_cost && { max_project_cost }),
    include_unknown_cost,
    ...(status && { status }),
    ...(districts && { districts }),
    ...(zoneExclusions && { zone_exclusions: zoneExclusions }),
    ...(createdAtStart && { date_created_start: createdAtStart }),
    ...(createdAtEnd && { date_created_end: createdAtEnd }),
    ...(keywords && { keywords }),
    ...(exclusions && { exclusions }),
    ...(triggers && { triggers }),
    ...(past_project_stage && { past_project_stage }),
    ...(exclude_past_project_stage && { exclude_past_project_stage }),
    ...(verticals && { verticals }),
    ...(verticalsExclusions && { verticals_exclusions: verticalsExclusions }),
    latest_activity_start: startDate,
    latest_activity_end: defaultEndDate,
    ...(real_estate_data_available && { real_estate_data_available }),
    ...(broker_data_available && { broker_data_available }),
    ...(document_url_available && { document_url_available }),
    ...(companies && { companies }),
    project_size: {
      min: project_size_min || null,
      max: project_size_max || null,
      include_unknown: !!project_size_include_unknown,
    },
    parcel_size: {
      min: parcel_size_min || null,
      max: parcel_size_max || null,
      include_unknown: !!parcel_size_include_unknown,
    },
    ...(work_types && { work_type_ids: work_types }),
    ...(work_types_exclusions && { work_type_ids_exclusions: work_types_exclusions }),
    ...(company_keywords && { company_keywords }),
    ...(company_keywords_exclusions && { company_keywords_exclusions }),
    ...(city_ids && { city_ids }),
    ...(county_codes && county_codes.length > 0 && { county_codes }),
    ...(mercator_metropolitan_ids && { mercator_metropolitan_ids }),
    company_types: company_stakeholder_types ? company_stakeholder_types.split(',') : [],
    company_types_exclusions: company_stakeholder_types_exclusions
      ? company_stakeholder_types_exclusions?.split(',')
      : [],
  };

  const { data } = await ElasticSearchFilterAPI.postFilterProjectPins({
    ...filterPayload,
    signal,
  });
  if (data.message && !data.data.results) {
    throw new Error(data.message);
  }

  return data.data.results || [];
}

type MapClusterRequest = MapPinRequest & {
  clusterResolution:
    | 'clustering_h4'
    | 'clustering_h5'
    | 'clustering_h6'
    | 'clustering_h7'
    | 'clustering_h8'
    | 'clustering_h9'
    | 'clustering_h10';
  cellsWeNeed: string[];
  mercator_metropolitan_ids?: string[] | null;
};

export async function fetchMapCluster({
  pathOrBounds,
  search,
  districts,
  zoneExclusions,
  createdAtStart,
  createdAtEnd,
  verticals,
  verticalsExclusions,
  triggers,
  past_project_stage,
  exclude_past_project_stage,
  min_project_cost,
  max_project_cost,
  include_unknown_cost,
  status,
  keywords,
  startDate,
  real_estate_data_available,
  broker_data_available,
  document_url_available,
  radius,
  exclusions,
  companies,
  project_size_min,
  project_size_max,
  project_size_include_unknown,
  parcel_size_min,
  parcel_size_max,
  parcel_size_include_unknown,
  work_types,
  work_types_exclusions,
  company_keywords,
  company_keywords_exclusions,
  city_ids,
  county_codes,
  mercator_metropolitan_id,
  mercator_metropolitan_ids,
  company_stakeholder_types,
  company_stakeholder_types_exclusions,
  clusterResolution,
  cellsWeNeed,
  raw_queries,
}: MapClusterRequest) {
  const defaultEndDate = getDefaultEndDate();

  let formattedBounds = pathOrBounds;
  if (typeof pathOrBounds !== 'string' && !pathOrBounds?.east) {
    formattedBounds = undefined;
  }

  const bbox =
    !formattedBounds || typeof formattedBounds === 'string'
      ? formattedBounds
      : formatBounds(formattedBounds);

  if (raw_queries) {
    const { data } = await ElasticSearchFilterAPI.postFilterClusters({
      bbox,
      raw_queries,
      ...(status && { status }),
      ...(createdAtStart && { date_created_start: createdAtStart }),
      ...(createdAtEnd && { date_created_end: createdAtEnd }),
      latest_activity_start: startDate,
      latest_activity_end: defaultEndDate,
      clusterResolution,
      cellsWeNeed,
    });

    if (!data) {
      throw new Error();
    }

    return data || [];
  }

  let metropolitanIds = {};

  if (mercator_metropolitan_ids) {
    metropolitanIds = { mercator_metropolitan_ids };
  } else if (mercator_metropolitan_id) {
    metropolitanIds = { mercator_metropolitan_id };
  }

  const filterPayload: ProjectFilterRequest = {
    bbox,
    ...metropolitanIds,
    ...(search && search.length > 0 && { radius: radius.radius, point: radius.point }),
    ...(min_project_cost && { min_project_cost }),
    ...(max_project_cost && { max_project_cost }),
    include_unknown_cost,
    ...(status && { status }),
    ...(districts && { districts }),
    ...(zoneExclusions && { zone_exclusions: zoneExclusions }),
    ...(createdAtStart && { date_created_start: createdAtStart }),
    ...(createdAtEnd && { date_created_end: createdAtEnd }),
    ...(keywords && { keywords }),
    ...(exclusions && { exclusions }),
    ...(triggers && { triggers }),
    ...(past_project_stage && { past_project_stage }),
    ...(exclude_past_project_stage && { exclude_past_project_stage }),
    ...(verticals && { verticals }),
    ...(verticalsExclusions && { verticals_exclusions: verticalsExclusions }),
    latest_activity_start: startDate,
    latest_activity_end: defaultEndDate,
    ...(real_estate_data_available && { real_estate_data_available }),
    ...(broker_data_available && { broker_data_available }),
    ...(document_url_available && { document_url_available }),
    ...(companies && { companies }),
    project_size: {
      min: project_size_min || null,
      max: project_size_max || null,
      include_unknown: !!project_size_include_unknown,
    },
    parcel_size: {
      min: parcel_size_min || null,
      max: parcel_size_max || null,
      include_unknown: !!parcel_size_include_unknown,
    },
    ...(work_types && { work_type_ids: work_types }),
    ...(work_types_exclusions && { work_type_ids_exclusions: work_types_exclusions }),
    ...(company_keywords && { company_keywords }),
    ...(company_keywords_exclusions && { company_keywords_exclusions }),
    ...(city_ids && { city_ids }),
    ...(county_codes && county_codes.length > 0 && { county_codes }),
    company_types: company_stakeholder_types ? company_stakeholder_types.split(',') : [],
    company_types_exclusions: company_stakeholder_types_exclusions
      ? company_stakeholder_types_exclusions?.split(',')
      : [],
  };

  const { data } = await ElasticSearchFilterAPI.postFilterClusters({
    ...filterPayload,
    clusterResolution,
    cellsWeNeed,
  });

  if (!data) {
    throw new Error();
  }

  return data || [];
}
